import '@/styles/jobsystem-layout.css';
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { IngameFlash, useIngameFlash } from '@/components/IngameFlash';
import { SimpleMenu } from '@/components/MenuGrid';
import { useLocale } from '@/context/LocaleContext';
import { itemIconFallbackUrl, resolveMediaUrl } from '@/lib/assetUrl';
import { resolveCharIdFromJwt } from '@/lib/axios';
import {
  useClaimJobReward,
  useJobSystem,
  type JobData,
  type JobLogRow,
  type JobSystemErrorKey,
} from '@/hooks/useJobSystem';

declare global {
  interface Window {
    bootstrap?: {
      Tooltip: new (
        el: Element,
        opts?: { placement?: string; trigger?: string },
      ) => { dispose: () => void };
    };
  }
}

const JOB_BAR_COLORS: Record<number, string> = {
  1: '#c5a15e',
  2: '#8f774d',
  3: '#e6c98a',
};

function formatNumber(value: number): string {
  return value.toLocaleString();
}

function resolveCharId(): number {
  return resolveCharIdFromJwt();
}

function getRewardState(jobData: JobData): 'incomplete' | 'claimable' | 'claimed' {
  if (jobData.claimedToday) return 'claimed';
  if (jobData.isCompleted && !jobData.claimedToday) return 'claimable';
  return 'incomplete';
}

function buildProgressTooltip(
  jobData: JobData,
  t: (key: string) => string,
): string {
  const jobNames: Record<number, string> = {
    1: t('job_merchant'),
    2: t('job_thief'),
    3: t('job_hunter'),
  };

  const orderedBreakdown: Record<number, number> = {};
  if (jobData.progressBreakdown[jobData.jobType] !== undefined) {
    orderedBreakdown[jobData.jobType] = jobData.progressBreakdown[jobData.jobType];
  }
  for (const [type, amount] of Object.entries(jobData.progressBreakdown)) {
    const n = Number(type);
    if (n !== jobData.jobType) {
      orderedBreakdown[n] = amount;
    }
  }

  const parts: string[] = [];
  for (const [type, amount] of Object.entries(orderedBreakdown)) {
    if (amount > 0) {
      parts.push(`${jobNames[Number(type)] ?? 'Unknown'}: ${formatNumber(amount)}`);
    }
  }
  parts.push(`Total: ${formatNumber(jobData.dailyProgress)}`);
  return parts.join(' | ');
}

function JobProgressCard({
  jobData,
  onClaim,
  claiming,
}: {
  jobData: JobData;
  onClaim: (jobType: number) => Promise<boolean>;
  claiming: boolean;
}) {
  const { t } = useLocale();
  const rewardState = getRewardState(jobData);
  const [localClaimed, setLocalClaimed] = useState(false);

  useEffect(() => {
    setLocalClaimed(jobData.claimedToday);
  }, [jobData.claimedToday]);

  const showClaimed = localClaimed || jobData.claimedToday;
  const visualState = showClaimed ? 'claimed' : rewardState;

  const orderedBreakdown = useMemo(() => {
    const result: Record<number, number> = {};
    if (jobData.progressBreakdown[jobData.jobType] !== undefined) {
      result[jobData.jobType] = jobData.progressBreakdown[jobData.jobType];
    }
    for (const [type, amount] of Object.entries(jobData.progressBreakdown)) {
      const n = Number(type);
      if (n !== jobData.jobType) {
        result[n] = amount;
      }
    }
    return result;
  }, [jobData.progressBreakdown, jobData.jobType]);

  const tooltipText = buildProgressTooltip(jobData, t);

  const handleRewardClick = () => {
    if (visualState !== 'claimable' || claiming) return;
    void onClaim(jobData.jobType).then((ok) => {
      if (ok) {
        setLocalClaimed(true);
      }
    });
  };

  const jobTypeLabel = t(`job_${jobData.jobTypeName}` as 'job_merchant');

  return (
    <div className="jobsystem-item">
      <div className="jobsystem-item-content">
        <div className="jobsystem-item-top">
          <div className="jobsystem-item-job-icon">
            <img src={resolveMediaUrl(jobData.icon_url)} alt={jobTypeLabel} />
          </div>
          <div className="jobsystem-item-info">
            <h4 className="jobsystem-item-name">
              {jobTypeLabel} - {t('job_level')} {jobData.jobLevel}
            </h4>
            <p className="jobsystem-item-description">{t('job_daily_progress')}</p>
          </div>
          <div className="jobsystem-item-reward-icon-wrapper">
            {jobData.reward?.icon_url ? (
              <button type="button" className={`jobsystem-item-reward-icon jobsystem-reward-img-${visualState}`} disabled={visualState !== 'claimable' || claiming} onClick={handleRewardClick} aria-label={`${jobData.reward.item_name ?? jobData.reward.CodeName128} x${jobData.reward.Quanty}`} >
                <img
                  src={resolveMediaUrl(jobData.reward.icon_url)}
                  alt="Reward"
                  style={{
                    cursor: visualState === 'claimable' && !claiming ? 'pointer' : 'default',
                    opacity: claiming ? 0.6 : 1,
                    pointerEvents: visualState === 'claimable' && !claiming ? 'auto' : 'none',
                  }}
                  title={`${jobData.reward.item_name ?? jobData.reward.CodeName128} x${jobData.reward.Quanty}`}
                  data-bs-toggle="tooltip"
                  data-bs-placement="top"
                  data-tooltip={`${jobData.reward.item_name ?? jobData.reward.CodeName128} x${jobData.reward.Quanty}`}
                  onError={(e) => {
                    e.currentTarget.onerror = null;
                    e.currentTarget.src = itemIconFallbackUrl();
                  }}
                />
                <span className="jobsystem-item-reward-badge">x{jobData.reward.Quanty}</span>
                {showClaimed && (
                  <span className="jobsystem-claimed-overlay">
                    <i className="ph-light ph-check-circle" aria-hidden />
                  </span>
                )}
              </button>
            ) : null}
          </div>
        </div>

        <div className="jobsystem-item-bottom">
          <div className="jobsystem-item-progress-info">
            <span className="jobsystem-status-text">
              {showClaimed
                ? t('job_claimed')
                : jobData.isCompleted
                  ? t('job_completed')
                  : t('job_in_progress')}
            </span>
            <span className="jobsystem-progress-numbers">
              {formatNumber(jobData.dailyProgress)} / {formatNumber(jobData.totalRequired)}{' '}
              {t('job_gold')}
            </span>
          </div>
          <div
            className="jobsystem-item-progress-bar-container"
          >
            <div
              style={{ display: 'flex', width: '100%', height: '100%' }}
              data-bs-toggle="tooltip"
              data-bs-placement="top"
              data-tooltip={tooltipText}
              title={tooltipText}
            >
              {Object.entries(orderedBreakdown).map(([type, amount]) => {
                if (amount <= 0) return null;
                const width =
                  jobData.totalRequired > 0 ? (amount / jobData.totalRequired) * 100 : 0;
                return (
                  <div
                    key={type}
                    style={{
                      width: `${width}%`,
                      backgroundColor: JOB_BAR_COLORS[Number(type)] ?? '#999',
                      height: '100%',
                    }}
                  />
                );
              })}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}

function JobLogsTable({ logs }: { logs: JobLogRow[] }) {
  const { t } = useLocale();

  if (logs.length === 0) {
    return (
      <section className="jobsystem-logs-container">
        <h3 className="jobsystem-logs-title">{t('job_delivery_history')}</h3>
        <div className="jobsystem-empty-logs"><i className="ph-light ph-package" aria-hidden="true" /><p>{t('job_no_deliveries_today')}</p></div>
      </section>
    );
  }

  return (
    <div className="jobsystem-logs-container">
      <h3 className="jobsystem-logs-title">{t('job_delivery_history')}</h3>
      <div className="jobsystem-logs-list">
        <table className="jobsystem-logs-table">
          <thead>
            <tr>
              <th>{t('job_route')}</th>
              <th>{t('job_gold_earned')}</th>
              <th>{t('job_delivery_time')}</th>
              <th>{t('job_delivery_hour')}</th>
            </tr>
          </thead>
          <tbody>
            {logs.map((log, idx) => (
              <tr key={`${log.DateEnd}-${idx}`}>
                <td>
                  {log.is_special_route && log.special_route_text ? (
                    <span className="jobsystem-log-city">
                      {log.special_route_text === 'protected_cargo'
                        ? t('job_protected_cargo')
                        : log.special_route_text === 'stolen_cargo'
                          ? t('job_stolen_cargo')
                          : `${log.origin_city_name} → ${log.target_city_name}`}
                    </span>
                  ) : (
                    <>
                      <span className="jobsystem-log-city">{log.origin_city_name}</span>
                      <span className="jobsystem-log-arrow">→</span>
                      <span className="jobsystem-log-city">{log.target_city_name}</span>
                    </>
                  )}
                </td>
                <td className="jobsystem-log-gold">
                  {formatNumber(log.RemaindGold)} {t('job_gold')}
                </td>
                <td className="jobsystem-log-time">
                  {log.delivery_time_minutes} {t('job_min')}
                </td>
                <td className="jobsystem-log-date">{log.delivery_time_formatted}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
}

const ERROR_KEYS: Record<JobSystemErrorKey, string> = {
  login_required: 'job_login_required',
  no_job_info: 'job_no_job_info',
  hwid_error: 'job_hwid_error',
  level_error: 'job_level_error',
  no_configuration: 'job_no_configuration',
};

const CLAIM_MSG_KEYS: Record<string, string> = {
  invalid_data: 'job_invalid_data',
  hwid_error: 'job_hwid_error',
  already_claimed_today: 'job_already_claimed_today',
  no_configuration: 'job_no_configuration',
  complete_progress_first: 'job_complete_progress_first',
  level_error: 'job_level_error',
  no_reward_for_level: 'job_no_reward_for_level',
  delivery_error: 'job_error',
  reward_claimed_success: 'job_reward_claimed_success',
};

export function JobSystemPage() {
  const { t } = useLocale();
  const charId = resolveCharId();
  const { data, isLoading, isError } = useJobSystem(charId);
  const claimMutation = useClaimJobReward(charId);
  const { flash, showFlash } = useIngameFlash(3000);
  const containerRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const root = containerRef.current;
    if (!root || !window.bootstrap?.Tooltip) return undefined;

    const triggers = root.querySelectorAll('[data-bs-toggle="tooltip"], .tooltipped');
    const instances = Array.from(triggers).map(
      (el) => new window.bootstrap!.Tooltip(el, { placement: 'top', trigger: 'hover' }),
    );

    return () => {
      instances.forEach((instance) => instance.dispose());
    };
  }, [data?.jobData, data?.logs]);

  const handleClaim = useCallback(
    async (jobType: number) => {
      try {
        const result = await claimMutation.mutateAsync(jobType);
        const msgKey = CLAIM_MSG_KEYS[result.message] ?? 'job_error';
        if (result.success) {
          showFlash(`${t('job_success')}: ${t(msgKey)}`, 'success');
        } else {
          showFlash(t(msgKey), 'danger');
        }
        return result.success;
      } catch {
        showFlash(t('job_network_error'), 'danger');
        return false;
      }
    },
    [claimMutation, showFlash, t],
  );

  const errorMessage = data?.error ? t(ERROR_KEYS[data.error] ?? 'job_error') : null;

  return (
    <div className="container-ingame job-system-page" ref={containerRef}>
      <IngameFlash flash={flash} />
      <div className="jobsystem-container">
        <SimpleMenu title={t('job_system')} />
        <div className="jobsystem-body">
          {isLoading && !data ? (
            <div className="jobsystem-empty" style={{ textAlign: 'center', padding: 40, color: '#999' }}>
              …
            </div>
          ) : isError ? (
            <div
              className="alert alert-danger"
              style={{
                marginBottom: 20,
                padding: 15,
                background: '#c00',
                color: '#fff',
                borderRadius: 5,
              }}
            >
              {t('job_network_error')}
            </div>
          ) : errorMessage ? (
            <div
              className="alert alert-danger"
              style={{
                marginBottom: 20,
                padding: 15,
                background: '#c00',
                color: '#fff',
                borderRadius: 5,
              }}
            >
              {errorMessage}
            </div>
          ) : data?.jobData ? (
            <>
              <JobProgressCard
                jobData={data.jobData}
                onClaim={handleClaim}
                claiming={claimMutation.isPending}
              />
              <JobLogsTable logs={data.logs} />
            </>
          ) : (
            <div className="jobsystem-empty" style={{ textAlign: 'center', padding: 40, color: '#999' }}>
              {t('job_no_job_info')}
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
